import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  getAllFacilitiesBranches,
  getFacilityBranches,
  getFacilityStaffs,
} from "../../../helpers/api";
import { resolveDefaultFacilityBranchId } from "../../../helpers/utilities";

export function useAppointmentLocation({ state, setState, user }) {
  const [isChangingLocation, setIsChangingLocation] = useState(false);

  const { data: networkBranches = [] } = useQuery({
    queryFn: getAllFacilitiesBranches,
    queryKey: ["facility-branches-network"],
  });

  const facilityOptions = useMemo(() => {
    const map = new Map();
    const addFacility = (facility) => {
      if (!facility?.id) return;
      map.set(facility.id, {
        id: facility.id,
        name:
          facility.facilityName ||
          facility.name ||
          facility.slug ||
          "Facility",
      });
    };
    (user?.facilities || []).forEach(addFacility);
    if (user?.facility) addFacility(user.facility);
    (networkBranches || []).forEach((branch) => {
      if (branch?.facility) addFacility(branch.facility);
    });
    return Array.from(map.values());
  }, [networkBranches, user]);

  const { data: facilityBranches = [] } = useQuery({
    queryFn: () => getFacilityBranches(state.facilityId),
    queryKey: ["facility-branches", state.facilityId],
    enabled: !!state.facilityId,
  });

  const branchesForFacility = useMemo(() => {
    if (facilityBranches?.length) return facilityBranches;
    return (networkBranches || []).filter(
      (branch) =>
        branch.facilityId === state.facilityId ||
        branch.facility?.id === state.facilityId
    );
  }, [facilityBranches, networkBranches, state.facilityId]);

  const { data: doctors = [] } = useQuery({
    queryFn: () => getFacilityStaffs(state.facilityId),
    queryKey: ["facility-staffs", state.facilityId],
    enabled: !!state.facilityId,
    keepPreviousData: false,
  });

  useEffect(() => {
    if (!state.facilityBranchId || state.facilityId || !networkBranches.length) {
      return;
    }
    const branch = networkBranches.find((b) => b.id === state.facilityBranchId);
    const facilityId =
      branch?.facilityId || branch?.facility?.id || null;
    if (facilityId) {
      setState((prev) => ({ ...prev, facilityId }));
    }
  }, [state.facilityBranchId, state.facilityId, networkBranches, setState]);

  useEffect(() => {
    if (!state.facilityId || !branchesForFacility.length) return;
    const branchStillValid = branchesForFacility.some(
      (b) => b.id === state.facilityBranchId
    );
    if (branchStillValid) return;
    const isUsersHomeFacility =
      state.facilityId === (user?.facility?.id || user?.facilityId);
    const defaultBranch =
      isUsersHomeFacility && user?.branchId
        ? user.branchId
        : resolveDefaultFacilityBranchId(branchesForFacility);
    if (defaultBranch) {
      setState((prev) => ({ ...prev, facilityBranchId: defaultBranch }));
    }
  }, [
    state.facilityId,
    state.facilityBranchId,
    branchesForFacility,
    user?.branchId,
    user?.facility?.id,
    user?.facilityId,
    setState,
  ]);

  const selectedFacilityName =
    facilityOptions.find((f) => f.id === state.facilityId)?.name ||
    user?.facility?.facilityName ||
    user?.facility?.name ||
    "—";

  const selectedBranchName =
    branchesForFacility.find((b) => b.id === state.facilityBranchId)?.name ||
    branchesForFacility.find((b) => b.id === state.facilityBranchId)
      ?.branchName ||
    "—";

  const useCurrentLocation = () => {
    setState((prev) => ({
      ...prev,
      facilityId: user?.facility?.id || user?.facilityId || null,
      facilityBranchId: user?.branchId || null,
      staffId: null,
    }));
    setIsChangingLocation(false);
  };

  return {
    facilityOptions,
    branchesForFacility,
    doctors,
    selectedFacilityName,
    selectedBranchName,
    isChangingLocation,
    setIsChangingLocation,
    useCurrentLocation,
  };
}
